import {
  checkSession,
  isAdmin,
  isConsentRequired,
  isPatient,
  isProfessional,
  isTenantAdmin,
  SessionState,
  sessionState,
  requiredIdentityProvider,
  targetRoute
} from 'src/services/session.js'
import { auth } from 'src/services/auth.js'
import { useRouter } from 'vue-router'
import { inject, onBeforeMount, watch } from 'vue'
import { setRouterAsReady } from 'src/router/index.js'
import logger from 'src/services/logger/index.js'
import { useProfessionalSessionFactory } from 'src/modules/core/composables/useProfessionalSession.js'
import { useAdminSessionFactory } from 'src/modules/core/composables/useAdminSession.js'
import { usePatientSessionFactory } from 'src/modules/core/composables/usePatientSession.js'
import { commonRoutes } from 'src/router/common-routes.js'
import { useTenantAdminSessionFactory } from 'src/modules/core/composables/useTenantAdminSession'

export const useSessionFactoryInjectionKey = 'useSessionFactoryInjectionKey'

export function useSessionFactory() {
  if (inject(useSessionFactoryInjectionKey, null) !== null) {
    throw new Error('Composable useSessionFactory already used in an ascendant. Use the composable "useSession" instead.')
  }

  const router = useRouter()
  const { onCreateProfessionalSession } = useProfessionalSessionFactory()
  const { onCreateAdminSession } = useAdminSessionFactory()
  const { onCreateTenantAdminSession } = useTenantAdminSessionFactory()
  const { onCreatePatientSession } = usePatientSessionFactory()

  let started = false

  async function startSession() {
    if (started) {
      logger.warn('Session already started')
      return
    }

    started = true

    router.clearRoutes()
    if (isProfessional.value) {
      await onCreateProfessionalSession()
    } else if (isPatient.value) {
      await onCreatePatientSession()
    } else if (isAdmin.value) {
      await onCreateAdminSession()
    } else if (isTenantAdmin.value) {
      await onCreateTenantAdminSession()
    }

    // After the others because of the "not-found" route
    commonRoutes.forEach(router.addRoute)
  }

  watch(sessionState, async () => {
    if(!sessionState.value || sessionState.value === SessionState.loading) {
      return
    }

    if (sessionState.value === SessionState.loggedIn) {
      await startSession()
    }

    setRouterAsReady()

    try {
      await router.replace(targetRoute.value ?? { path: window.location.pathname })
    } catch (error) {
      logger.warn(error)
      // Ignore it
    }
  })

  router.beforeEach((to, from, next) => {
    function enforceRoute(routeName) {
      if (to.name === routeName) {
        next()
      } else {
        next({ name: routeName })
      }
    }

    const routeAnonymous = to.matched?.some(route => route.meta?.anonymous === true)
    if (isConsentRequired.value) {
      enforceRoute('accept-cgu')
    } else if (sessionState.value === SessionState.unknownAuthError) {
      enforceRoute('auth-error')
    } else if (sessionState.value !== SessionState.loggedIn && !routeAnonymous) {
      auth.login(null, [requiredIdentityProvider.value])
    } else {
      next()
    }
  })

  onBeforeMount(async () => {
    await checkSession()
  })
}
